import { Geometry } from '@turf/helpers';
import Feature from 'ol/Feature';
import { WKB } from 'ol/format';
import WKT from 'ol/format/WKT';
import { LineString, Point, Polygon } from 'ol/geom';
import { transform } from 'ol/proj';
import { LayerRepresentation } from 'src/app/core/types/camada.types';
import { isAvaliation } from 'src/app/core/utils/utils';
import Terraformer from 'terraformer-wkt-parser';
import { MapData } from '../types/map.types';

export type ProjectionType = 'EPSG:3857' | 'EPSG:4326' | 'EPSG:31982';

export enum PROJECTION {
  WGS84 = 'EPSG:4326',
  WEBMERCATOR = 'EPSG:3857',
}

const mapDataRegExp =
  /^(-?[0-9]*(?:\.[0-9]*)?),(-?[0-9]*(?:\.[0-9]*)?),([0-9]{1,2})$/;

export function isValidMapDataStr(str: string) {
  return mapDataRegExp.test(str);
}

export function parseMapDataStr(str: string): {
  data: MapData;
  ok: boolean;
} {
  const err = {
    ok: false,
    data: {
      center: [0, 0],
      zoom: 0,
    } as MapData,
  };

  if (!isValidMapDataStr(str)) {
    return err;
  }

  const match = mapDataRegExp.exec(str);

  // This check is unecessary.
  // Put here only because of TS.
  if (!match) {
    return err;
  }

  return {
    ok: true,
    data: {
      center: [+match[1], +match[2]],
      zoom: +match[3],
    },
  };
}

export function serializeMapData(data: MapData) {
  const transformedCenter = transform(
    data.center,
    PROJECTION.WEBMERCATOR,
    PROJECTION.WGS84
  );
  return `${transformedCenter[0]},${transformedCenter[1]},${data.zoom}`;
}

export function WKTtoFeature(
  wkt: string,
  fromProjection?: ProjectionType,
  toProjection?: ProjectionType
) {
  const parserWKT = new WKT();
  const wktOptions = {
    featureProjection: fromProjection || 'EPSG:3857',
    dataProjection: toProjection || 'EPSG:4326',
  };
  const feature = parserWKT.readFeature(wkt, wktOptions);
  return feature;
}

export function WKBtoFeature(
  wkb: string,
  fromProjection?: ProjectionType,
  toProjection?: ProjectionType
) {
  const parserWKB = new WKB();
  const wktOptions = {
    featureProjection: fromProjection || 'EPSG:3857',
    dataProjection: toProjection || 'EPSG:4326',
  };
  const feature = parserWKB.readFeature(wkb, wktOptions);
  return feature;
}

export const wktToFeature = (wkt: string) => Terraformer.parse(wkt);
export const FeatureToWkt = (geom: Geometry) => Terraformer.convert(geom);

export function OLFeatureToWKT(
  feature: Feature<any>,
  fromProjection?: ProjectionType,
  toProjection?: ProjectionType
) {
  const parserWKT = new WKT();
  const wktOptions = {
    featureProjection: fromProjection || 'EPSG:3857',
    dataProjection: toProjection || 'EPSG:4326',
  };
  const stringWKT = parserWKT.writeFeature(feature, wktOptions);
  return stringWKT;
}

export function OLFeatureToWKB(
  feature: Feature<any>,
  fromProjection?: ProjectionType,
  toProjection?: ProjectionType
) {
  const parserWKB = new WKB();
  const wkbOptions = {
    featureProjection: fromProjection || 'EPSG:3857',
    dataProjection: toProjection || 'EPSG:4326',
  };
  const stringWKB = parserWKB.writeFeature(feature, wkbOptions);
  return stringWKB;
}

export const vectorTileFeatureToOLFeature = (vtFeature: any): Feature<any> => {
  const vtFeatureType = (vtFeature as any).getType();
  let coordinates = (vtFeature as any).flatCoordinates_;
  let geometry: Point | LineString | Polygon = new Point(coordinates);

  if (vtFeatureType !== 'Point') {
    coordinates = coordinates.reduce((acc, curr, index) => {
      if (index % 2) {
        acc = [...acc, [coordinates[index - 1], curr]];
      }
      return acc;
    }, []);

    geometry =
      vtFeatureType === 'LineString'
        ? new LineString(coordinates)
        : new Polygon([coordinates]);
  }
  const olFeature = new Feature();
  olFeature.setGeometry(geometry);
  olFeature.setProperties({
    ...vtFeature.getProperties(),
    id: vtFeature.getId(),
  });
  olFeature.setId(`projetos_camadas_geometrias.${vtFeature.getId()}`);

  return olFeature;
};

export function WKBToWKT(wkb: string) {
  const feature = WKBtoFeature(wkb);
  const wkt = OLFeatureToWKT(feature);
  return wkt;
}

export function getLegendColorByVM2(legend: any, vm2: number) {
  // there isn't a legend when the only one project and it's icad.
  if (!legend) {
    return '#FF0000';
  }

  const legendLength = legend.length;
  let colorToSet = '#000';
  for (let i = 0; i < legendLength; i++) {
    const { min, max, cor } = legend[i];
    if (i === 0) {
      if (vm2 < max) {
        colorToSet = cor;
      }
    } else if (i === legendLength - 1) {
      if (vm2 >= min) {
        colorToSet = cor;
      }
    } else {
      if (vm2 >= min && vm2 < max) {
        colorToSet = cor;
      }
    }
  }
  return colorToSet;
}

export function getLegendItemByVM2(legend: any, vm2: number) {
  const legendLength = legend.length;
  let legendItem = {
    min: 0,
    max: 0,
    cor: '#000000',
  };

  for (let i = 0; i < legendLength; i++) {
    const { min, max } = legend[i];
    if (i === 0) {
      if (vm2 < max) {
        legendItem = {
          ...legend[i],
          min: null,
        };
      }
    } else if (i === legendLength - 1) {
      if (vm2 >= min) {
        legendItem = {
          ...legend[i],
          max: null,
        };
      }
    } else {
      if (vm2 >= min && vm2 < max) {
        legendItem = legend[i];
      }
    }
  }
  return legendItem;
}

export function getFeatureVm2(feature: any) {
  if (isAvaliation(feature.properties.nome_camada)) {
    const avaliation = JSON.parse(feature.properties.GeoJson);
    // return Math.round(avaliation.terrain.VM2COR);
    return avaliation.terrain.VM2COR;
  }

  return (
    feature.properties['VM2 Cor'] || feature.properties.VM2
    // Math.round(feature.properties['VM2 Cor']) ||
    // Math.round(feature.properties.VM2)
  );
}

export function getInsertionPeriodInMs(insertionPeriod: string): number {
  switch (insertionPeriod) {
    case '1 dia':
      return 8.64e7;
    case '1 semana':
      return 6.048e8;
    case '1 mês':
      return 2.628e9;
    case '6 meses':
      return 1.577e10;
    case '1 ano':
      return 3.154e10;
  }

  return -1;
}

export const LAYER_REPRESENTATIONS_THAT_APPEAR_ON_LIST = [
  LayerRepresentation.PVG,
  LayerRepresentation.COLLAB_SAMPLES,
  // LayerRepresentation.SECTOR
];

export function isLayerVisibleOnTheList(layerRepresentation: string) {
  return (LAYER_REPRESENTATIONS_THAT_APPEAR_ON_LIST as string[]).includes(
    layerRepresentation
  );
}

export const highlightStyle = {
  weight: 8,
  opacity: 1,
  color: '#F9B303',
};

export const highlightStyleRadius = {
  weight: 5,
  color: '#F9B303',
  fillOpacity: 0.6,
  radius: 18,
};

export const resetHighlightStyle = {
  weight: 0.8,
  color: '#fff',
  fillOpacity: 0.2,
};

export const highlightLotesStyle = {
  weight: 4,
  color: '#2084cb',
  fillOpacity: 0.3,
};

export const resetHighlightLotesStyle = {
  weight: 2,
  color: '#add8e6',
  fillOpacity: 0.1,
};
